import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "../../../assets/css/GameField.css"
import GameOverModal from "../../../components/GameOverModal";
import GameWonModal from "../../../components/GameWonModal";
import { faL } from "@fortawesome/free-solid-svg-icons";
import { useDarkMode } from "../../../context/DarkModeProvider";
import { useLanguage } from "context/LanguageProvider";

const AVLInsertion = () => {
    const [currentStage, setCurrentStage] = useState(1);
    const [treeNodes, setTreeNodes] = useState([]);
    const [availableNodes, setAvailableNodes] = useState([]);
    const [draggedNode, setDraggedNode] = useState(null);
    const [message, setMessage] = useState("");
    const [gameState, setGameState] = useState("playing");
    const [lives, setLives] = useState(3);
    const [nodesToInsert, setNodesToInsert] = useState([]);
    const [trapPositions, setTrapPositions] = useState([]);
    const [showHint, setShowHint] = useState(false);
    const [wrongAttempts, setWrongAttempts] = useState(0);
    const [showGameOver, setShowGameOver] = useState(false);
    const [showGameWon, setShowGameWon] = useState(false);
    const { darkMode } = useDarkMode();
    const { selLanguage, toggleLanguage, isLoading } = useLanguage();

    const renderNode = (nodeValue, index) => {
        const isTrapPosition = trapPositions.includes(index);
        const shouldBeNull = stages[currentStage].correctSolution[index] === null;
        const isOriginalNode = stages[currentStage].initialTree[index] !== null;

        return (
            <motion.div
                key={index}
                whileHover={nodeValue !== null ? { scale: 1.1 } : {}}
                whileTap={nodeValue !== null ? { scale: 0.95 } : {}}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDropOnNode(index)(e)}
                draggable={nodeValue !== null}
                onDragStart={(e) => nodeValue && handleDragStart(e, nodeValue, index)}
                style={{
                    width: "55px",
                    height: "55px",
                    borderRadius: "50%",
                    backgroundColor: nodeValue !== null
                        ? isOriginalNode ? "#1d4ed8" : "#3b82f6" : "#e3e4e6ff",
                    color: "#eeeeeeff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "bold",
                    fontSize: "14px",
                    cursor: nodeValue !== null ? "grab" : "default",
                    border: "2px solid rgba(255,255,255,0.1)",
                    transition: "all 0.3s ease",
                    position: "relative",
                    boxShadow: "inset 0 2px 4px rgba(0,0,0,0.1)"
                }}
                title={
                    nodeValue
                        ? selLanguage ? `Κόμβος ${nodeValue} - Σύρε για μετακίνηση` : `Node ${nodeValue} - Drag to move`
                        : isTrapPosition && shouldBeNull
                            ? selLanguage ? "ΠΑΓΙΔΑ: Πρέπει να μείνει κενό!" : "TRAP: Must remain empty!"
                            : selLanguage ? "Κενή θέση - Ρίξε εδώ έναν κόμβο" : "Empty spot - Drop a node here"
                }
            >
                {nodeValue}
            </motion.div>
        );
    };

    // Νέες βοηθητικές συναρτήσεις για SVG layout (όπως στο deletionAvl)
    const getNodeLevel = (index) => {
        return Math.floor(Math.log2(index + 1));
    };

    const getNodePositions = (level, nodeIndex) => {
        const baseSpacing = 600;
        const levelSpacing = baseSpacing / Math.pow(2, level);
        const nodesInLevel = Math.pow(2, level);
        const indexInLevel = nodeIndex - (Math.pow(2, level) - 1);
        const startOffset = -(nodesInLevel - 1) * levelSpacing / 2;
        return {
            x: startOffset + indexInLevel * levelSpacing,
            y: level * 100
        };
    };

    const renderEdges = () => {
        const edges = [];
        for (let i = 0; i < treeNodes.length; i++) {
            if (treeNodes[i] === null && treeNodes[i] !== 0) continue; // skip empty parents visually
            const leftChildIndex = 2 * i + 1;
            const rightChildIndex = 2 * i + 2;

            const parentLevel = getNodeLevel(i);
            const parentPos = getNodePositions(parentLevel, i);

            if (leftChildIndex < treeNodes.length) {
                const childPos = getNodePositions(getNodeLevel(leftChildIndex), leftChildIndex);
                edges.push(
                    <line
                        key={`edge-${i}-${leftChildIndex}`}
                        x1={parentPos.x}
                        y1={parentPos.y + 30}
                        x2={childPos.x}
                        y2={childPos.y + 30}
                        stroke={treeNodes[leftChildIndex] !== null ? "#64748b" : "#94a3b8"}
                        strokeWidth="2"
                        opacity={treeNodes[leftChildIndex] !== null ? "0.8" : "0.35"}
                        strokeDasharray={treeNodes[leftChildIndex] !== null ? "0" : "5,5"}
                    />
                );
            }

            if (rightChildIndex < treeNodes.length) {
                const childPos = getNodePositions(getNodeLevel(rightChildIndex), rightChildIndex);
                edges.push(
                    <line
                        key={`edge-${i}-${rightChildIndex}`}
                        x1={parentPos.x}
                        y1={parentPos.y + 30}
                        x2={childPos.x}
                        y2={childPos.y + 30}
                        stroke={treeNodes[rightChildIndex] !== null ? "#64748b" : "#94a3b8"}
                        strokeWidth="2"
                        opacity={treeNodes[rightChildIndex] !== null ? "0.8" : "0.35"}
                        strokeDasharray={treeNodes[rightChildIndex] !== null ? "0" : "5,5"}
                    />
                );
            }
        }
        return edges;
    };



    const stages = {
        1: {
            description: selLanguage
                ? "Εισάγετε τους κόμβους στις κατάλληλες θέσεις του AVL tree!"
                : "Insert the nodes into the correct positions of the AVL tree!",
            initialTree: [20, 10, 30, null, null, null, null],
            nodesToInsert: [5, 25],
            correctSolution: [20, 10, 30, 5, null, 25, null],
            trapPositions: [4, 6], // Μόνο 2 παγίδες
            treeSize: 7,
        },
        2: {
            description: selLanguage
                ? "Εισάγετε τους κόμβους στις κατάλληλες θέσεις του AVL tree!"
                : "Insert the nodes into the correct positions of the AVL tree!",
            initialTree: [30, 20, 40, null, null, null, null],
            nodesToInsert: [10, 25, 35],
            correctSolution: [30, 20, 40, 10, 25, 35, null],
            trapPositions: [6], // Μόνο 1 παγίδα στο τέλος
            treeSize: 7,
        },
        3: {
            description: selLanguage
                ? "Εισάγετε τους κόμβους στις κατάλληλες θέσεις του AVL tree!"
                : "Insert the nodes into the correct positions of the AVL tree!",

            // Ήδη ισορροπημένο AVL ύψους 3
            initialTree: [
                40,
                20, 60,
                10, 30, 50, 70,
                null, null, null, null, null, null, null, null
            ],
            nodesToInsert: [5, 25, 45, 65],
            correctSolution: [
                40,
                20, 60,
                10, 30, 50, 70,
                5, null, 25, null, 45, null, 65, null
            ],
            trapPositions: [8, 11, 12, 14],

            treeSize: 15,
        }

    };


    useEffect(() => {
        initializeStage(currentStage);
    }, [currentStage]);

    const initializeStage = (stage) => {
        const stageData = stages[stage];
        setTreeNodes([...stageData.initialTree]);
        setAvailableNodes([...stageData.nodesToInsert]);
        setNodesToInsert(stageData.nodesToInsert);
        setTrapPositions(stageData.trapPositions);

        setGameState("playing");
        setShowHint(false);
        setWrongAttempts(0);
    };

    const handleDragStart = (e, node, fromIndex) => {
        setDraggedNode({ node, fromIndex });
        e.dataTransfer.setData("text/plain", JSON.stringify({ node, fromIndex }));
    };

    const handleDragOver = (e) => {
        e.preventDefault();

    };

    const handleDropOnNode = (toIndex) => (e) => {
        e.preventDefault();
        const data = JSON.parse(e.dataTransfer.getData("text/plain"));
        const { node, fromIndex } = data;

        if (fromIndex === toIndex) return;

        // 👉 Αν ρίχνουμε από available nodes
        if (fromIndex === 'available') {
            // τι είχε ΠΡΙΝ το δέντρο σε αυτή τη θέση;
            const existingNode = treeNodes[toIndex];

            // 1. ενημέρωσε το δέντρο
            setTreeNodes((prev) => {
                const updated = [...prev];
                updated[toIndex] = node;   // είτε ήταν null είτε είχε άλλο κόμβο, απλά το γράφουμε
                return updated;
            });

            // 2. ενημέρωσε τα available
            setAvailableNodes((prev) => {
                const updated = [...prev];

                const idx = updated.indexOf(node);
                if (idx !== -1) {
                    updated.splice(idx, 1);
                }

                if (existingNode !== null && existingNode !== undefined) {
                    updated.push(existingNode);
                }

                return updated;
            });
        }
        else {
            setTreeNodes((prev) => {
                const updated = [...prev];

                if (updated[toIndex] === null) {
                    updated[toIndex] = node;
                    updated[fromIndex] = null;
                } else {
                    const temp = updated[toIndex];
                    updated[toIndex] = node;
                    updated[fromIndex] = temp;
                }

                return updated;
            });
            // Εδώ ΔΕΝ πειράζουμε τα availableNodes
        }
    };


    const handleDropOnAvailable = (e) => {
        e.preventDefault();
        const data = JSON.parse(e.dataTransfer.getData("text/plain"));
        const { node, fromIndex } = data;

        if (fromIndex === 'available') return;

        setTreeNodes((prev) => {
            const updated = [...prev];
            updated[fromIndex] = null;
            return updated;
        });

        setAvailableNodes((prev) => {
            if (!prev.includes(node)) {
                return [...prev, node];
            }
            return prev;
        });
    };

    const handleDragStartFromAvailable = (e, node) => {
        setDraggedNode({ node, fromIndex: 'available' });
        e.dataTransfer.setData("text/plain", JSON.stringify({ node, fromIndex: 'available' }));
    };

    const checkSolution = () => {
        const currentSolution = stages[currentStage].correctSolution;
        const isCorrect = currentSolution.every((val, i) => treeNodes[i] === val);

        if (isCorrect) {
            if (currentStage === 3) {
                setGameState("completed");
                setShowGameWon(true);
                setMessage(selLanguage ? "Συγχαρητήρια! Ολοκληρώσατε όλα τα στάδια με επιτυχία!" : "Congratulations! You have successfully completed all stages!");
            } else {
                setMessage(selLanguage ? ` Τέλεια! Προχωρώντας στο στάδιο ${currentStage + 1}` : ` Great! Moving to stage ${currentStage + 1}`);
                setTimeout(() => {
                    setCurrentStage(currentStage + 1);
                }, 2000);
            }
            setShowHint(false);
            setWrongAttempts(0);
        } else {
            const newLives = lives - 1; // Υπολόγισε τις νέες ζωές πρώτα
            setLives(newLives);
            setWrongAttempts(wrongAttempts + 1);
            setShowHint(true);

            if (newLives <= 0) { // Χρήση των νέων ζωών
                setGameState("lost");
                setShowGameOver(true); // ✨ ΚΡΙΣΙΜΗ ΔΙΟΡΘΩΣΗ: Εμφάνιση του modal
                setMessage(selLanguage ? "Έχασες όλες τις ζωές σου. Δοκίμασε ξανά!" : "You have lost all your lives. Try again!");
            } else {
                setMessage(selLanguage ? `Λάθος διάταξη! Απομένουν ${newLives} ζωές.` : `Incorrect arrangement! ${newLives} lives remaining.`);
            }
        }
    };

    const resetGame = () => {
        setCurrentStage(1);
        setLives(3);
        setGameState("playing");
        setMessage("");
        setShowHint(false);
        setWrongAttempts(0);
        setShowGameOver(false);
        setShowGameWon(false);
        initializeStage(1);
    };


    const renderTreeLevel = (level, startIndex, nodeCount) => {
        const nodes = [];
        for (let i = 0; i < nodeCount; i++) {
            if (startIndex + i < treeNodes.length) {
                nodes.push(renderNode(treeNodes[startIndex + i], startIndex + i));
            }
        }

        return (
            <div key={level} style={{
                display: "flex",
                justifyContent: "center",
                gap: `${Math.max(20, 60 - level * 15)}px`,
                flexWrap: "wrap"
            }}>
                {nodes}
            </div>
        );
    };

    const renderSvgNode = (nodeValue, index) => {
        const isRoot = index === 0;
        const level = getNodeLevel(index);
        const pos = getNodePositions(level, index);
        const isEmpty = nodeValue === null;

        return (
            <g key={`svgnode-${index}`}>
                <foreignObject
                    x={pos.x - 27.5}
                    y={pos.y}
                    width="55"
                    height="55"
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDropOnNode(index)(e)}
                >
                    <div style={{ width: "100%", height: "100%" }}>
                        {/* επαναχρησιμοποίηση renderNode που περιέχει τα handlers */}
                        {renderNode(nodeValue, index)}
                    </div>
                </foreignObject>
            </g>
        );
    };


    const renderTree = () => {
        const tree = treeNodes;
        const maxLevel = Math.max(0, Math.floor(Math.log2(Math.max(1, tree.length))));
        const svgWidth = 800;
        const svgHeight = (maxLevel + 2) * 100 + 60;

        return (
            <div style={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
                overflowX: "auto",
                marginTop: "10px"
            }}>
                <svg
                    width={svgWidth}
                    height={svgHeight}
                    viewBox={`${-svgWidth / 2} 0 ${svgWidth} ${svgHeight}`}
                    style={{ minWidth: "600px", height: svgHeight }}
                >
                    {renderEdges()}
                    {tree.map((node, idx) => renderSvgNode(node, idx))}
                </svg>
            </div>
        );
    };

    useEffect(() => {
        if (message && !message.includes("Εισάγετε")) {
            const timer = setTimeout(() => {
                setMessage("");
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [message]);

    const containerStyle = {
        minHeight: "90vh",
        background: darkMode ? "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #101724ff 100%)" : "var(--lightModeGameBackground)",
        padding: "30px 10px",
        borderRadius: "10px",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        overflowX: "hidden",
    };

    const pronStyle = {
        borderRadius: "20px",
        maxWidth: "770px",
        background: "linear-gradient(135deg, rgba(59, 130, 246, 0.4), rgba(29, 78, 216, 0.4))",
        width: "90%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "15px 20px",
        margin: "0 auto 20px",
        color: "white",
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    };

    return (
        <div
            className='main-content'
            style={containerStyle}>
            <GameOverModal
                isVisible={showGameOver}
                onPlayAgain={resetGame}

            />

            <GameWonModal
                isVisible={showGameWon}
                onPlayAgain={resetGame}

            />
            <div className="max-w-7xl mx-auto" style={{ width: "100%" }}>

                {/* Lives και Stage Info */}
                <div style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "15px",
                    flexWrap: "wrap",
                    gap: "10px",
                    marginTop: "15px"
                }}>
                    <div style={{
                        color: darkMode ? "white" : "#1e293b",
                        padding: "12px 24px",
                        borderRadius: "12px",
                        fontWeight: "700",
                        fontSize: "18px",
                        border: darkMode ? "2px solid rgba(255, 255, 255, 0.2)" : "2px solid #1e293b",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px"
                    }}>
                        {selLanguage ? `Επίπεδο ${currentStage}/3` : `Stage ${currentStage}/3`}
                    </div>

                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        marginRight: "10px"
                    }}>

                        <div style={{ display: "flex", gap: "6px" }}>
                            {[1, 2, 3].map((heartNum) => (
                                <div
                                    key={heartNum}
                                    style={{
                                        fontSize: "24px",
                                        filter: lives >= heartNum
                                            ? "drop-shadow(0 4px 8px rgba(220, 38, 38, 0.4))"
                                            : "grayscale(100%) opacity(0.3)",
                                    }}
                                >
                                    ❤️
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Description */}
                <div style={pronStyle}>
                    <div>
                        {stages[currentStage].description}
                    </div>
                </div>

                {/* Tree Visualization */}
                <div style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "24px",
                    marginBottom: "24px",
                    maxWidth: "700px",
                    margin: "0 auto 24px auto",
                    width: "100%"
                }}>
                    {renderTree()}
                </div>

                {/* Available Nodes */}
                <div
                    style={{
                        marginBottom: "24px",
                        background: "#8fb2c280",
                        padding: "20px 24px",
                        borderRadius: "16px",
                        border: "2px dashed rgba(148, 163, 184, 0.5)",
                        boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)",
                        maxWidth: "600px",
                        margin: "0 auto 24px auto",
                        width: "100%",
                        minHeight: "120px",
                        transition: "all 0.3s ease"
                    }}
                    onDragOver={handleDragOver}
                    onDrop={handleDropOnAvailable}
                >
                    <h3 style={{
                        fontWeight: "600",
                        marginBottom: "16px",
                        color: "white",
                        textAlign: "center"
                    }}>
                        {selLanguage ? `Διαθέσιμοι κόμβοι (${availableNodes.length})` : `Available Nodes (${availableNodes.length})`}
                    </h3>
                    <div style={{
                        display: "flex",
                        gap: "12px",
                        flexWrap: "wrap",
                        justifyContent: "center",
                        minHeight: "60px",
                        alignItems: "center"
                    }}>
                        {availableNodes.length === 0 ? (
                            <div style={{
                                color: "rgba(255,255,255,0.7)",
                                fontStyle: "italic",
                                fontSize: "14px"
                            }}>
                                {selLanguage ? "Σύρε κόμβους εδώ για να τους αφαιρέσεις από το δέντρο" : "Drag nodes here to remove them from the tree"}
                            </div>
                        ) : (
                            availableNodes.map((node, index) => (
                                <motion.div
                                    key={`${node}-${index}`}
                                    draggable
                                    onDragStart={(e) => handleDragStartFromAvailable(e, node)}
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.95 }}
                                    style={{
                                        width: "48px",
                                        height: "48px",
                                        borderRadius: "50%",
                                        backgroundColor: "#3b82f6",
                                        color: "white",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontWeight: "bold",
                                        cursor: "grab",
                                        boxShadow: "0 4px 8px rgba(59, 130, 246, 0.3)",
                                        border: "2px solid rgba(245, 245, 245, 0.5)",
                                    }}
                                >
                                    {node}
                                </motion.div>
                            ))
                        )}
                    </div>
                </div>

                {/* Messages */}
                {/* Messages */}
                <AnimatePresence>
                    {message && (
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            style={{
                                textAlign: "center",
                                padding: "20px",
                                borderRadius: "16px",
                                marginBottom: "20px",
                                background: message.includes(selLanguage ? "Τέλεια" : "Great") ||
                                    message.includes(selLanguage ? "Συγχαρητήρια" : "Congratulations")
                                    ? "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)"
                                    : message.includes(selLanguage ? "Λάθος" : "Incorrect") ||
                                        message.includes(selLanguage ? "Έχασες" : "lost")
                                        ? "linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)"
                                        : "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)",
                                border: `2px solid ${message.includes(selLanguage ? "Τέλεια" : "Great") ||
                                    message.includes(selLanguage ? "Συγχαρητήρια" : "Congratulations")
                                    ? "#10b981" :
                                    message.includes(selLanguage ? "Λάθος" : "Incorrect") ||
                                        message.includes(selLanguage ? "Έχασες" : "lost")
                                        ? "#ef4444" : "#3b82f6"
                                    }`,
                                color: message.includes(selLanguage ? "Τέλεια" : "Great") ||
                                    message.includes(selLanguage ? "Συγχαρητήρια" : "Congratulations")
                                    ? "#059669" :
                                    message.includes(selLanguage ? "Λάθος" : "Incorrect") ||
                                        message.includes(selLanguage ? "Έχασες" : "lost")
                                        ? "#dc2626" : "#1d4ed8",
                                fontWeight: 600,
                                fontSize: "1rem",
                                maxWidth: "600px",
                                margin: "0 auto 20px auto",
                                width: "100%"
                            }}
                        >
                            {message}
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Buttons */}
                <div style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "12px",
                    width: "100%",
                    maxWidth: "500px",
                    margin: "0 auto",
                    flexWrap: "wrap"
                }}>
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={checkSolution}
                        disabled={gameState !== "playing"}
                        className="commonGameCheckButton"
                    >
                        {selLanguage ? "Έλεγχος" : "Check"}
                    </motion.button>

                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => initializeStage(currentStage)}
                        className="commonGameDeleteButton"
                    >
                        {selLanguage ? "Επαναφορά" : "Reset"}
                    </motion.button>

                    {(gameState === "lost" || gameState === "completed") && (
                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={resetGame}
                            style={{
                                background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                                color: "white",
                                border: "none",
                                borderRadius: "16px",
                                padding: "14px 28px",
                                fontSize: "1rem",
                                fontWeight: 600,
                                cursor: "pointer",
                                boxShadow: "0 10px 25px rgba(16, 185, 129, 0.3)",
                                transition: "all 0.3s ease",
                                flex: 1,
                                maxWidth: "150px"
                            }}
                        >
                            {selLanguage ? "Νέο Παιχνίδι" : "New Game"}
                        </motion.button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AVLInsertion;